import { weekdays } from "../findDateByTargetWeekdays";
import { MAX_DAYS, MOCK_DURATION_SECONDS } from "./constants";
import { buildOrderedLessons } from "./lessonOrdering";
import { buildModuleInfo, buildQueue } from "./queueBuilder";
import {
  isCompleted,
  getProgress,
  getStatus,
  allocateItemsForDay,
  itemSortKey,
  buildDay,
  addDays,
  isStudyDay,
} from "./dayAllocator";

export {
  MOCK_DURATION_SECONDS,
  MOCK_QUESTIONS_PER_MODULE,
  MOCK_MINUTES_PER_QUESTION,
  MAX_DAYS,
  OVERFLOW_BUFFER_SECONDS,
} from "./constants";

export default function generateStudyPlan({
  lessons,
  levels,
  lessonsUsers,
  studyGoal,
  courseId,
  userId,
}) {
  if (!studyGoal) return { error: "No study goal found", days: [], completedLessons: [] };

  const weekdayAttrs = studyGoal.weekdayStudyGoalsAttributes || [];
  if (weekdayAttrs.length === 0) return { error: "No weekdays selected", days: [], completedLessons: [] };

  const weekdayIndexes = weekdayAttrs.map((w) => weekdays.indexOf(w.weekday));
  const dailyBudget = studyGoal.dayStudyDurationSeconds;

  // Build lessonsUsers lookup for current user
  const lessonsUsersMap = {};
  Object.values(lessonsUsers).forEach((lu) => {
    if (lu.userId === userId) {
      lessonsUsersMap[lu.lessonId] = lu;
    }
  });

  // 1. Ordered lessons
  const orderedLessons = buildOrderedLessons(lessons, levels, courseId);

  // 2. Position map for sorting
  const positionMap = {};
  orderedLessons.forEach((l, i) => {
    positionMap[l.id] = i;
  });

  // 3. Split completed/remaining
  const today = new Date();
  const todayStart = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );
  const todayEnd = new Date(todayStart);
  todayEnd.setDate(todayEnd.getDate() + 1);

  const completedLessons = [];
  const remainingLessons = [];
  const completedTodayLessons = [];

  orderedLessons.forEach((lesson) => {
    const lu = lessonsUsersMap[lesson.id];
    if (isCompleted(lu)) {
      completedLessons.push(lesson);
      const updatedAt = lu.updatedAt ? new Date(lu.updatedAt) : null;
      if (updatedAt && updatedAt >= todayStart && updatedAt < todayEnd) {
        completedTodayLessons.push(lesson);
      }
    } else {
      remainingLessons.push(lesson);
    }
  });

  // 4. Module info
  const { moduleMap, parentDetails } = buildModuleInfo(levels, courseId);

  // 5. Build queue from remaining lessons
  const queue = buildQueue(remainingLessons, moduleMap, parentDetails, courseId);

  // Pre-compute max lesson position per module for mock sorting
  const maxPosByModule = {};
  orderedLessons.forEach((lesson, idx) => {
    const modName = moduleMap[lesson.levelId]?.name;
    if (modName) maxPosByModule[modName] = idx;
  });
  queue.forEach((item) => {
    if (item.type === "mock") {
      item._maxLessonPos = maxPosByModule[item.moduleName] ?? 0;
    }
  });

  // 6. Allocate into days
  const days = [];
  let currentDate = new Date(today);
  let dayIndex = 0;
  let lastModule = null;

  // Today's card (always shown)
  {
    let todayItems;

    if (completedTodayLessons.length === 0) {
      todayItems = allocateItemsForDay(queue, dailyBudget);
    } else {
      const budgetUsed = completedTodayLessons.reduce(
        (sum, l) => sum + (l.duration || 0),
        0
      );
      const todayRemaining = allocateItemsForDay(queue, dailyBudget, {
        usedSeconds: budgetUsed,
        allowOverflow: false,
      });
      const todayCompletedItems = completedTodayLessons.map((lesson) => ({
        type: "lesson",
        lesson,
        moduleName: moduleMap[lesson.levelId]?.name,
        modulePosition: moduleMap[lesson.levelId]?.position,
        overBudget: false,
      }));

      const allTodayItems = [...todayCompletedItems, ...todayRemaining];
      allTodayItems.sort(
        (a, b) =>
          itemSortKey(a, positionMap) - itemSortKey(b, positionMap)
      );
      todayItems = allTodayItems;
    }

    dayIndex++;
    const result = buildDay(
      today,
      dayIndex,
      todayItems,
      true,
      lastModule,
      dailyBudget,
      positionMap,
      lessonsUsersMap
    );
    days.push(result.day);
    lastModule = result.lastModule;

    currentDate = addDays(today, 1);
  }

  // Future days
  while (queue.length > 0 && days.length < MAX_DAYS) {
    if (isStudyDay(currentDate, weekdayIndexes)) {
      dayIndex++;
      const dayItems = allocateItemsForDay(queue, dailyBudget);
      const result = buildDay(
        currentDate,
        dayIndex,
        dayItems,
        false,
        lastModule,
        dailyBudget,
        positionMap,
        lessonsUsersMap
      );
      days.push(result.day);
      lastModule = result.lastModule;
    }
    currentDate = addDays(currentDate, 1);
  }

  const truncated = queue.length > 0;

  // 7. Serialize completed lessons
  const serializedCompleted = completedLessons.map((lesson) => {
    const lu = lessonsUsersMap[lesson.id];
    return {
      type: "lesson",
      id: lesson.id,
      name: lesson.name,
      durationSeconds: lesson.duration || 0,
      progress: getProgress(lu),
      status: getStatus(lu),
      overBudget: false,
      courseId,
      levelId: lesson.levelId,
      completedAt: lu?.updatedAt || null,
    };
  });

  // 8. Total remaining seconds (lessons + mocks)
  const remainingModuleNames = [
    ...new Set(
      remainingLessons
        .map((l) => moduleMap[l.levelId]?.name)
        .filter(Boolean)
    ),
  ];
  const mockSeconds = remainingModuleNames.reduce((sum, name) => {
    const details = parentDetails[name];
    return sum + (details && details.questionsCount > 0 ? MOCK_DURATION_SECONDS : 0);
  }, 0);
  const lessonSeconds = remainingLessons.reduce(
    (sum, l) => sum + (l.duration || 0),
    0
  );

  return {
    completedLessons: serializedCompleted,
    days,
    truncated: truncated || (queue.length > 0),
    totalStudyDaysCount: days.length,
    totalRemainingLessonsCount: remainingLessons.length,
    totalRemainingSeconds: lessonSeconds + mockSeconds,
    studyGoal: {
      dailyBudgetSeconds: dailyBudget,
      weekdays: weekdayAttrs.map((w) => w.weekday),
    },
  };
}
